import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import Header from "./Header";

const BASE_URL =
  "https://522gqh3w18.execute-api.ap-south-1.amazonaws.com/Prod/Coupon";

function AllCoupons() {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  // ── Filter & Pagination state ─────────────────────────────────────────────
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // ── Fetch all coupons ─────────────────────────────────────────────────────
  const fetchCoupons = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${BASE_URL}/GetAllCoupon`, {
        headers: {
          Authorization: localStorage.getItem("SLNAdminToken") ?? "",
        },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);
      const data = await res.json();
      console.log("📦 GetAllCoupon response:", data);
      const list = Array.isArray(data) ? data : (data.data ?? data.coupons ?? []);
      setCoupons(list);
    } catch (err) {
      console.error("❌ Error fetching coupons:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCoupons();
  }, []);

  // ── Delete coupon ─────────────────────────────────────────────────────────
  const handleDelete = async (id, code) => {
    const result = await Swal.fire({
      title: "Delete Coupon?",
      html: `Are you sure you want to delete coupon <strong>${code}</strong>?<br/><span style="color:#888;font-size:13px">This action cannot be undone.</span>`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel",
      reverseButtons: true,
      customClass: {
        popup: "rounded-4",
        confirmButton: "px-4 rounded-3",
        cancelButton: "px-4 rounded-3",
      },
    });

    if (!result.isConfirmed) return;

    setDeletingId(id);
    try {
      const res = await fetch(
        `${BASE_URL}/DeleteCoupon?id=${encodeURIComponent(id)}`,
        {
          method: "DELETE",
          headers: {
            Authorization: localStorage.getItem("SLNAdminToken") ?? "",
          },
        }
      );

      const data = await res.json().catch(() => ({}));
      console.log("🗑️ DeleteCoupon response:", data);
      if (!res.ok) throw new Error(data?.message ?? `HTTP ${res.status}`);

      setCoupons((prev) => prev.filter((c) => (c._id ?? c.id) !== id));

      await Swal.fire({
        title: "Deleted!",
        text: "Coupon has been removed successfully.",
        icon: "success",
        timer: 2000,
        showConfirmButton: false,
        customClass: { popup: "rounded-4" },
      });
    } catch (err) {
      console.error("❌ Delete failed:", err);
      await Swal.fire({
        title: "Delete Failed",
        text: err.message || "Something went wrong. Please try again.",
        icon: "error",
        confirmButtonColor: "#dc3545",
        customClass: { popup: "rounded-4", confirmButton: "px-4 rounded-3" },
      });
    } finally {
      setDeletingId(null);
    }
  };

  // ── Helpers ───────────────────────────────────────────────────────────────
  const formatDate = (raw) => {
    if (!raw) return "—";
    const d = new Date(raw);
    if (isNaN(d)) return raw;
    return d.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const isExpired = (end_date) => {
    if (!end_date) return false;
    return new Date(end_date) < new Date();
  };

  const isActive = (start_date, end_date) => {
    const now = new Date();
    return new Date(start_date) <= now && new Date(end_date) >= now;
  };

  const statusBadge = (coupon) => {
    if (isExpired(coupon.end_date))
      return "bg-danger-subtle text-danger border-danger-subtle";
    if (isActive(coupon.start_date, coupon.end_date))
      return "bg-success-subtle text-success border-success-subtle";
    return "bg-warning-subtle text-warning border-warning-subtle";
  };

  const statusLabel = (coupon) => {
    if (isExpired(coupon.end_date)) return "Expired";
    if (isActive(coupon.start_date, coupon.end_date)) return "Active";
    return "Upcoming";
  };

  // ── Filter & Pagination derived data ──────────────────────────────────────
  const filteredCoupons = coupons.filter((c) => {
    if (!searchTerm.trim()) return true;
    const q = searchTerm.toLowerCase();
    return (
      String(c.code ?? "").toLowerCase().includes(q) ||
      String(c.description ?? "").toLowerCase().includes(q) ||
      String(c.discount_type ?? "").toLowerCase().includes(q) ||
      String(c.discount_value ?? "").toLowerCase().includes(q) ||
      statusLabel(c).toLowerCase().includes(q)
    );
  });

  const totalPages = Math.max(1, Math.ceil(filteredCoupons.length / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const startIdx = (safePage - 1) * pageSize;
  const paginatedCoupons = filteredCoupons.slice(startIdx, startIdx + pageSize);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, pageSize]);

  const getPageNumbers = () => {
    const pages = [];
    const maxButtons = 5;
    let start = Math.max(1, safePage - Math.floor(maxButtons / 2));
    let end = Math.min(totalPages, start + maxButtons - 1);
    if (end - start + 1 < maxButtons) {
      start = Math.max(1, end - maxButtons + 1);
    }
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <Header />

      <section>
        <div className="container py-3">
          <div className="row">
            <div className="col-12">
              <div className="pb-3 d-flex flex-column flex-md-row gap-3 align-items-md-center justify-content-between">
                <div className="d-flex flex-column">
                  <h4 className="mb-0 lh-1 fw-medium text-dark">
                    All Coupons{" "}
                    {!loading && !error && (
                      <span className="text-secondary">({coupons.length})</span>
                    )}
                  </h4>
                  <p className="m-0 lh-sm text-secondary ls-sm">
                    Manage Coupons / Discounts / Promotions
                  </p>
                </div>
                <div>
                  <Link
                    to="/manage/add-coupon"
                    className="btn btn-primary fw-normal px-5 py-1 rounded-3"
                  >
                    Add Coupon
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* ── Loading ── */}
          {loading && (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading…</span>
              </div>
              <p className="mt-2 text-secondary">Fetching coupons…</p>
            </div>
          )}

          {/* ── Error ── */}
          {!loading && error && (
            <div className="alert alert-danger rounded-3 d-flex align-items-center gap-2">
              <i className="bi bi-exclamation-triangle-fill fs-5"></i>
              <div>
                <strong>Failed to load coupons.</strong> {error}
                <button
                  className="btn btn-sm btn-outline-danger ms-3 rounded-3"
                  onClick={fetchCoupons}
                >
                  Retry
                </button>
              </div>
            </div>
          )}

          {/* ── Search & Page Size ── */}
          {!loading && !error && (
            <div className="row g-2 align-items-center mb-3">
              <div className="col-md-6 col-12">
                <div className="input-group">
                  <span className="input-group-text bg-white">
                    <i className="bi bi-search"></i>
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search by code, description, discount, status..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  {searchTerm && (
                    <button
                      className="btn btn-outline-secondary"
                      type="button"
                      onClick={() => setSearchTerm("")}
                      title="Clear"
                    >
                      <i className="bi bi-x-lg"></i>
                    </button>
                  )}
                </div>
              </div>
              <div className="col-md-3 col-6">
                <select
                  className="form-select"
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                >
                  <option value={5}>5 per page</option>
                  <option value={10}>10 per page</option>
                  <option value={25}>25 per page</option>
                  <option value={50}>50 per page</option>
                  <option value={100}>100 per page</option>
                </select>
              </div>
              <div className="col-md-3 col-6 text-md-end text-secondary small">
                Showing {filteredCoupons.length === 0 ? 0 : startIdx + 1}–
                {Math.min(startIdx + pageSize, filteredCoupons.length)} of{" "}
                {filteredCoupons.length}
              </div>
            </div>
          )}

          {/* ── Table ── */}
          {!loading && !error && (
            <div className="table-responsive border-0 overflow-y-hidden bg-white rounded-4 mb-6">
              <table className="table mb-0 text-nowrap table-centered">
                <thead className="table-light">
                  <tr>
                    <th className="py-2" style={{ width: "4%" }}>No.</th>
                    <th className="lh-1 py-2" style={{ width: "56%" }}>
                      Coupon Details
                      <i className="bi bi-ticket-perforated ms-2 text-dark"></i>
                      <br />
                      <span className="fs-8 fw-normal text-secondary">
                        Displaying Information as per Record
                      </span>
                    </th>
                    <th className="lh-1 py-2" style={{ width: "20%" }}>
                      Validity
                      <i className="bi bi-calendar-range ms-2 text-dark"></i>
                      <br />
                      <span className="fs-8 fw-normal text-secondary">
                        Start &amp; End Date
                      </span>
                    </th>
                    <th className="lh-1 py-2" style={{ width: "20%" }}>
                      Action
                      <i className="bi bi-person-gear ms-2 text-dark"></i>
                      <br />
                      <span className="fs-8 fw-normal text-secondary">
                        Role-Specific Action Panel
                      </span>
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {filteredCoupons.length === 0 ? (
                    <tr>
                      <td
                        colSpan={4}
                        className="text-center py-5 text-secondary"
                      >
                        <i className="bi bi-ticket-perforated fs-2 d-block mb-2"></i>
                        {coupons.length === 0
                          ? "No coupons found."
                          : "No coupons match your search."}
                      </td>
                    </tr>
                  ) : (
                    paginatedCoupons.map((coupon, idx) => {
                      const id             = coupon._id ?? coupon.id;
                      const code           = coupon.code ?? "—";
                      const description    = coupon.description ?? "—";
                      const discount_type  = coupon.discount_type ?? "—";
                      const discount_value = coupon.discount_value ?? 0;
                      const min_purchase   = coupon.min_purchase_amount ?? null;
                      const max_discount   = coupon.max_discount_amount ?? null;
                      const usage_limit    = coupon.usage_limit ?? null;
                      const used_count     = coupon.used_count ?? coupon.usedCount ?? 0;
                      const start_date     = coupon.start_date ?? null;
                      const end_date       = coupon.end_date ?? null;
                      const isDeleting     = deletingId === id;

                      return (
                        <tr
                          key={id}
                          style={{ opacity: isDeleting ? 0.5 : 1 }}
                        >
                          <td>{startIdx + idx + 1}.</td>

                          {/* ── Coupon Details ── */}
                          <td className="lh-sm py-4">
                            <div className="d-flex align-items-center gap-2 mb-1">
                              {/* Code pill */}
                              <span
                                className="badge bg-dark text-white px-3 py-2 rounded-3 fs-6 fw-semibold"
                                style={{ letterSpacing: "0.08em" }}
                              >
                                {code}
                              </span>
                              {/* Status badge */}
                              <span
                                className={`badge border px-2 py-1 rounded-3 small ${statusBadge(coupon)}`}
                              >
                                {statusLabel(coupon)}
                              </span>
                            </div>

                            <span className="text-dark">
                              <strong>Description:</strong>
                            </span>{" "}
                            {description}
                            <br />
                            <span className="text-dark">
                              <strong>Discount:</strong>
                            </span>{" "}
                            <span className="fw-semibold text-primary">
                              {discount_type === "percentage"
                                ? `${discount_value}% off`
                                : `₹${discount_value} off`}
                            </span>
                            {discount_type === "percentage" && max_discount && (
                              <span className="text-secondary ms-1 small">
                                (max ₹{max_discount})
                              </span>
                            )}
                            <br />
                            {min_purchase != null && (
                              <>
                                <span className="text-dark">
                                  <strong>Min. Purchase:</strong>
                                </span>{" "}
                                ₹{min_purchase}
                                <br />
                              </>
                            )}
                            <span className="text-dark">
                              <strong>Usage:</strong>
                            </span>{" "}
                            {used_count}
                            {usage_limit != null && (
                              <span className="text-secondary">
                                {" "}/ {usage_limit}
                              </span>
                            )}
                            {usage_limit != null && (
                              <>
                                {" "}
                                <span className="text-secondary small">
                                  ({Math.round((used_count / usage_limit) * 100)}% used)
                                </span>
                              </>
                            )}
                          </td>

                          {/* ── Validity ── */}
                          <td className="lh-sm">
                            <span className="text-dark">
                              <strong>Start:</strong>
                            </span>{" "}
                            {formatDate(start_date)}
                            <br />
                            <span className="text-dark">
                              <strong>End:</strong>
                            </span>{" "}
                            <span
                              className={
                                isExpired(end_date) ? "text-danger" : ""
                              }
                            >
                              {formatDate(end_date)}
                            </span>
                          </td>

                          {/* ── Actions ── */}
                          <td>
                            <div className="d-flex gap-1 align-items-start">
                              <button
                                className="btn p-0 border-0 bg-transparent"
                                title="Delete"
                                disabled={isDeleting}
                                onClick={() => handleDelete(id, code)}
                              >
                                <div className="icon-shape icon-md bg-light-danger rounded-circle">
                                  {isDeleting ? (
                                    <span
                                      className="spinner-border spinner-border-sm text-danger"
                                      role="status"
                                    />
                                  ) : (
                                    <i className="bi bi-trash fs-4 text-dark"></i>
                                  )}
                                </div>
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* ── Pagination ── */}
          {!loading && !error && filteredCoupons.length > 0 && totalPages > 1 && (
            <nav aria-label="Coupons pagination" className="mt-3">
              <ul className="pagination justify-content-center mb-0 flex-wrap">
                <li className={`page-item ${safePage === 1 ? "disabled" : ""}`}>
                  <button
                    className="page-link"
                    onClick={() => setCurrentPage(1)}
                    disabled={safePage === 1}
                  >
                    <i className="bi bi-chevron-double-left"></i>
                  </button>
                </li>
                <li className={`page-item ${safePage === 1 ? "disabled" : ""}`}>
                  <button
                    className="page-link"
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={safePage === 1}
                  >
                    <i className="bi bi-chevron-left"></i>
                  </button>
                </li>
                {getPageNumbers().map((num) => (
                  <li
                    key={num}
                    className={`page-item ${num === safePage ? "active" : ""}`}
                  >
                    <button
                      className="page-link"
                      onClick={() => setCurrentPage(num)}
                    >
                      {num}
                    </button>
                  </li>
                ))}
                <li
                  className={`page-item ${safePage === totalPages ? "disabled" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() =>
                      setCurrentPage((p) => Math.min(totalPages, p + 1))
                    }
                    disabled={safePage === totalPages}
                  >
                    <i className="bi bi-chevron-right"></i>
                  </button>
                </li>
                <li
                  className={`page-item ${safePage === totalPages ? "disabled" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={safePage === totalPages}
                  >
                    <i className="bi bi-chevron-double-right"></i>
                  </button>
                </li>
              </ul>
            </nav>
          )}
        </div>
      </section>
    </>
  );
}

export default AllCoupons;
